import FieldController from './field.controller';

/**
 * Extendable subclass of FieldController that exposes an automatically-shifted dateValueShiftedForEventTimezone
 * property that is parsed from field value.
 * Should be used for all date/time fields that operate on a Javascript Date object
 *
 * Inbound data flow (API -> app data model -> field):
 *
 *    FieldResponseService.create() called with API value; parses and sets FieldResponse.value to a Date object:
 *      If Date or DateTime field type, call DateTimeService.parseDateWithTimezoneOrServerTimezone() to set value (returns Date object)
 *      If Time field type, call DateTimeService.parseTimeWithTimezoneOrServerTimezone() to set value (returns Date object)
 *      (both will parse Date object relative to event timezone, falling back to server timezone)
 *
 *    FieldResponse.currentValue initialized when FieldResponse.value set
 *
 *    FieldWithDateValueController.value (getter) passes value from FieldResponse.currentValue
 *
 *    FieldWithDateValueController.dateValue (getter) passes value from FieldWithDateController.value
 *
 *    FieldWithDateValueController.dateValueShiftedForEventTimezone (getter) adjusts .dateValue to account for event timezone difference.
 *      Date is no longer accurate, but when displayed in the local browser timezone, it will match the correct value in the event timezone.
 *
 * Outbound data flow (field -> app data model -> API)
 *
 *    FieldWithDateValueController.dateValueShiftedForEventTimezone (setter) reverses the timezone shift and sets .dateValue
 *
 *    FieldWithDateValueController.dateValue (setter) passes value to .value (and parses date if it's not already a date)
 *
 *    FieldWithDateValueController.value (setter inherited from FieldController) passes value to FieldResponse.currentValue
 *
 *    FieldResponse.storeCurrentValue() called when validation happens; updates FieldResponse.value from FieldResponse.currentValue
 *
 *    FieldResponseService.serialize() serializes based on field type:
 *      If DateTime, call DateTimeService.serializeDateTimeForAPI()
 *      If Date, call DateTimeService.serializeDateForAPI()
 *      if Time, call DateTimeService.serializeTimeForAPI()
 *      (each of these will serialize date object INTO event timezone or server timezone, then strip out timezone part per server format)
 *
 */
class FieldWithDateValueController extends FieldController {
  constructor($scope, $q, $translate, $log, $timeout, DateTimeService, LogService) {
    "ngInject";
    super($scope, $q, $translate, $log, $timeout);
    this.DateTimeService = DateTimeService;
    this.LogService = LogService;
  }

  $onInit() {
    this.dateValue = this.value;
    super.$onInit();
  }

  get dateValue() {
    return this.value;
  }

  set dateValue(dateValue) {
    this.value = this.DateTimeService.parseDateWithTimezoneOrServerTimezone(dateValue, this.eventTimezoneName);
    this._dateValueShiftedForEventTimezone = null;
    console.debug(`DateTimeFieldController set dateValue to '${this.value}' from input '${dateValue}'`, this.value, dateValue);
  }

  get dateValueShiftedForEventTimezone() {
    const dateValue = this.dateValue;
    if (!this._dateValueShiftedForEventTimezone) {
      let shiftedDate;
      if (this.eventTimezoneName && dateValue) {
        shiftedDate = this.DateTimeService.shiftDateObjectFromLocal(dateValue, this.eventTimezoneName);
      } else {
        shiftedDate = dateValue;
      }
      this._dateValueShiftedForEventTimezone = shiftedDate;
    }
    // this.LogService.debug(`get dateValueShiftedForEventTimezone() dateValue: ${dateValue} returning shiftedDate: ${this._dateValueShiftedForEventTimezone}`);
    return this._dateValueShiftedForEventTimezone;
  }

  set dateValueShiftedForEventTimezone(dateValue) {
    let unshiftedDate;
    if (this.eventTimezoneName) {
      unshiftedDate = this.DateTimeService.unshiftDateObjectFromLocal(dateValue, this.eventTimezoneName);
    } else {
      unshiftedDate = dateValue;
    }
    this.LogService.debug(`set dateValueShiftedForEventTimezone(${dateValue}) set unshiftedDate to ${unshiftedDate}`);
    this.dateValue = unshiftedDate;
    this._dateValueShiftedForEventTimezone = dateValue;
  }

  /**
   * Return the date value, shifted so that it matches the event timezone.
   *
   * Example:
   *
   *   Event timezone is 'America/Chicago'
   *   Local timezone is 'America/Los_Angeles'
   *
   *   Datetime sent from the API is '2017-03-01 12:00:00' which we assume is relative to the event's timezone
   *
   *   DateTimeService.parseDateWithTimezone() gives us a Date object that represents '2017-03-01 12:00:00 CST -0600'
   *   However, Javascript Date objects are just seconds since epoch (Jan 1 1970 00:00:00 UTC), and do not have a
   *   specific timezone. They are generally displayed either in the local browser timezone or in UTC.
   *
   *   When printed, the datetime we've parsed with the event timezone would likely be displayed either as UTC or local
   *   time:
   *     '2017-03-01 18:00:00Z' (UTC) or
   *     '2017-03-01 10:00:00 PST -0800' (PST)
   *
   *   The bootstrap-ui and bootstrap-ui-datetime-picker libraries generally display the date/time in local time, and
   *   do not display the timezone. So, if we pass our parsed datetime directly into the library, it will display:
   *     '2017-03-01 10:00:00'
   *
   *   However, we would prefer it to display the time in the event timezone, which was 12:00.
   *
   *   So we need to "fake" this by adjusting for the time zone offset between our local timezone and the event
   *   timezone.
   *
   *   shiftDateFromLocalTimezoneToAppearAsTimezone(date, 'America/Chicago')
   *

   *     (note that Date objects are just seconds since epoch, so do not actually have any specific timezone.
   *
   *

   *   Datetime is parsed in the event timezone, so the JS Date object is '2017-03-01 12:00:00 CST -0600'
   *     (which is identical to '2017-03-01 10:00:00 PST -0800')
   *
   *   We want to shift the date so that it appears to display the correct date/time relative to the event, even though
   *   we're in a different timezone. This way, bootstrap-ui-datetime-picker does not need to do any timezone conversion
   *   itself aside from displaying in the local timezone (which is its default behavior).
   *
   *
   *
   */
}

export default FieldWithDateValueController;
